import { useState, useEffect } from "react";
import { db, storage, functions } from "../firebase";
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable } from "firebase/storage";
import { useData } from "../context/DataContext";
import "./addpost.css";
import { useUser } from "../context/UserContext";

export default function AddPost() {
  const { user } = useUser();
  const { categoriesData, addPost } = useData();
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [urls, setUrls] = useState(["", ""]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadMethod, setUploadMethod] = useState("file"); // 'file' or 'youtube'
  const [uploadedFileName, setUploadedFileName] = useState(""); // Add state for file name
  
  // Form state
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [selectedSubCatID, setSelectedSubCatID] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [socialMediaLink, setSocialMediaLink] = useState("");
  const [youtubeLink, setYoutubeLink] = useState("");
  const handSelectedSubCategory = (e) => {
    setSelectedSubCatID(e.target.selectedOptions[0].getAttribute("subcategory_id"));
    setSelectedSubcategory(e.target.value);
  };
  // Get available subcategories based on selected category
  const getSubcategories = () => {
    if (!selectedCategory || !categoriesData) return [];
    const category = categoriesData.find((cat) => cat.id === selectedCategory);
    return category ? category.subcategories || [] : [];
  };

  // Function to remove spacing and special characters from filename
  const removeSpacingAndStrangeLetters = (name) => {
    return name.replace(/[^a-zA-Z0-9.]/g, "").replace(/\s+/g, "");
  };

  // Function to generate thumbnail from video
  const generateThumbnail = (videoFile) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      video.autoplay = true;
      video.muted = true;
      video.src = URL.createObjectURL(videoFile);

      video.onloadeddata = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const maxHeight = 400;
        const scale = maxHeight / video.videoHeight;
        canvas.width = video.videoWidth * scale;
        canvas.height = maxHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(video.src);
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to generate thumbnail"));
          }
        }, "image/png");
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error("Error loading video"));
      };
    });
  };

  // Handle file selection and upload
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      setError("No file selected");
      setUploadLoading(false);
      setUploadProgress(0);
      setUploadedFileName(""); // Reset file name
      return;
    }

    setUploadLoading(true);
    setUploadProgress(0.0001);
    setError("");
    setUploadedFileName(file.name); // Store the file name

    try {
      // Generate thumbnail
      const thumbnailBlob = await generateThumbnail(file);
      // Prepare Firebase storage references
      const fileName = removeSpacingAndStrangeLetters(file.name);
      const videoRef = ref(storage, `videos/${fileName}`);
      const thumbnailRef = ref(storage, `thumbnails/${fileName}.png`);

      // Upload video with progress tracking
      const uploadTask = uploadBytesResumable(videoRef, file, {
        contentType: file.type, // 'video/mp4' or 'video/mov'
      });

      // Track upload progress
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = snapshot.bytesTransferred / snapshot.totalBytes;
          setUploadProgress(progress === 0 ? 0.0001 : progress);
        },
        (error) => {
          setError(`Upload failed: ${error.message}`);
          setUploadLoading(false);
          setUploadProgress(0);
        },
        async () => {
          // Upload thumbnail after video
          await uploadBytes(thumbnailRef, thumbnailBlob, { contentType: "image/png" });

          // Get download URLs
          const videoUrl = await getDownloadURL(videoRef);
          const thumbnailUrl = await getDownloadURL(thumbnailRef);

          setUrls([videoUrl, thumbnailUrl]);
          setUploadLoading(false);
          setUploadProgress(0);
        }
      );
    } catch (e) {
      setError(`Error: ${e.message}`);
      setUploadLoading(false);
      setUploadProgress(0);
      setUrls(["", ""]);
      setUploadedFileName(""); // Reset file name on error
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith("video/")) {
        handleFileChange({ target: { files: [file] } });
      } else {
        setError("Only video formats are allowed");
      }
    }
  };
  const generateYoutubeThumbnail = (youtubeLink) => {
    const videoID = youtubeLink.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/))([^&?/]+)/)?.[1];
    if (videoID) return `https://img.youtube.com/vi/${videoID}/0.jpg`;
    else return "https://placehold.co/600x400?text=No+Thumbnail";
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(""); // Clear any previous errors

    // Validate required fields
    if (!selectedCategory || !selectedSubcategory || !title) {
      setError("Please fill in all required fields (Category, Subcategory, and Title)");
      return;
    }

    // Validate upload method specific requirements
    if (uploadMethod === "file" && !urls[0]) {
      setError("Please upload a video file");
      return;
    }

    if (uploadMethod === "youtube" && !youtubeLink) {
      setError("Please provide a valid YouTube link");
      return;
    }

    // Validate YouTube URL format if using YouTube method
    if (uploadMethod === "youtube") {
      const youtubeRegex =
        /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]+)(\S*)?$/;
      if (!youtubeRegex.test(youtubeLink)) {
        setError("Please provide a valid YouTube video or shorts URL");
        return;
      }
    }

    // Validate title / description length (same limits as mobile app)
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    if (trimmedTitle.length < 8 || trimmedTitle.length > 35) {
      setError("Title should be between 8 - 35 characters");
      return;
    }
    if (
      trimmedDescription.length > 0 &&
      (trimmedDescription.length < 8 || trimmedDescription.length > 125)
    ) {
      setError("Description should be between 8 - 125 characters");
      return;
    }

    try {
      const newPost = {
        category: selectedCategoryName,
        subcategory: selectedSubCatID,
        title: trimmedTitle,
        description: trimmedDescription,
        url: uploadMethod === "file" ? urls[0] : youtubeLink.trim(),
        thumbnail: uploadMethod === "file" ? urls[1] : generateYoutubeThumbnail(youtubeLink),
        date_uploaded: new Date(),
        reports: [],
        is_pending: false,
        uploader_id: user.uid,
        uploader_details: { email: user.email, name: user.displayName },
        social_media_link: socialMediaLink.trim(),
        views: 0,
        upload_type: uploadMethod, // Add upload type for future reference
      };

      await addPost(newPost);
      resetForm();
    } catch (e) {
      setError(`Error creating post: ${e.message}`);
    }
  };
  const resetForm = () => {
    setSelectedCategory("");
    setSelectedCategoryName("");
    setSelectedSubcategory("");
    setYoutubeLink("");
    setSelectedSubCatID("");
    setTitle("");
    setDescription("");
    setSocialMediaLink("");
    setUrls(["", ""]);
    setUploadProgress(0);
    setError("");
    setUploadMethod("file"); // Reset to default upload method
    setIsDragging(false); // Reset drag state
    setUploadedFileName(""); // Reset file name
  };
  return (
    <div className="addpost-container">
      <h4>
        <strong className="Users_mainText__yPmxu">Add Post</strong>
      </h4>

      <form onSubmit={handleSubmit} className="addpost-form">
        <div className="form-group">
          <label htmlFor="category">Category:</label>
          <select
            id="category"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setSelectedCategoryName(e.target.selectedOptions[0].getAttribute("catName"));
              setSelectedSubCatID("");
              setSelectedSubcategory("");
            }}
            required
          >
            <option value="">Select a category</option>
            {categoriesData &&
              categoriesData.map((category) => (
                <option key={category.id} value={category.id} catName={category.categoryName}>
                  {category.categoryName}
                </option>
              ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="subcategory">Subcategory:</label>
          <select
            id="subcategory"
            value={selectedSubcategory}
            onChange={(e) => handSelectedSubCategory(e)}
            disabled={!selectedCategory}
            required
          >
            <option value="">Select a subcategory</option>
            {getSubcategories().map((subcategory) => (
              <option key={subcategory.id} subcategory_id={subcategory.id} value={subcategory.id}>
                {subcategory.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="title">Title:</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={35}
            placeholder="Enter post title"
          />
          <small>{35 - title.length} characters remaining</small>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description:</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter post description"
            rows="2"
            maxLength={125}
          />
          <small>{125 - description.length} characters remaining</small>
        </div>

        <div className="form-group">
          <label htmlFor="uploadMethod">Upload Method:</label>
          <select id="uploadMethod" value={uploadMethod} onChange={(e) => setUploadMethod(e.target.value)} required>
            <option value="file">Upload Video File</option>
            <option value="youtube">YouTube Link</option>
          </select>
        </div>

        {uploadMethod === "file" && (
          <div className="form-group">
            <label htmlFor="video">Video:</label>
            <div
              className={`custom-file-upload ${isDragging ? "dragging" : ""} ${uploadLoading ? "disabled" : ""}`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="upload-icon">
                <i className="fas fa-cloud-upload-alt">📤</i>
              </div>
              <div className="upload-text">
                {uploadLoading ? "Uploading..." : "Drag and drop video here or click to browse"}
              </div>
              <input id="video" type="file" accept="video/*" onChange={handleFileChange} disabled={uploadLoading} />
            </div>
          </div>
        )}

        {uploadMethod === "youtube" && (
          <div className="form-group">
            <label htmlFor="youtubeLink">Youtube Link:</label>
            <input
              type="text"
              id="youtubeLink"
              value={youtubeLink}
              onChange={(e) => setYoutubeLink(e.target.value)}
              pattern="^(https:\/\/youtu\.be).+|^https:\/\/www\.youtube\.com.+"
              placeholder="https://youtu.be/ or https://www.youtube.com/"
              title="Youtube Only"
              required={uploadMethod === "youtube"}
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="businessLink">Business Link:</label>
          <input
            type="text"
            id="businessLink"
            value={socialMediaLink}
            onChange={(e) => setSocialMediaLink(e.target.value)}
            placeholder="Enter business link (optional)"
          />
        </div>

        {uploadLoading && (
          <div className="progress-container">
            <p>Uploading: {(uploadProgress * 100).toFixed(2)}%</p>
            <progress className="progress" value={uploadProgress.toFixed(2)} max="1" />
          </div>
        )}

        {error && <p className="error">{error}</p>}

        {/* Show preview based on upload method */}
        {uploadMethod === "file" && urls[0] && urls[1] && (
          <div className="preview">
            <p>
              Upload successful! <span className="file-name">{uploadedFileName}</span>
            </p>
            {/* <div className="preview-links">
              <a href={urls[0]} target="_blank" rel="noopener noreferrer">
                View Video
              </a>
              <a href={urls[1]} target="_blank" rel="noopener noreferrer">
                View Thumbnail
              </a>
            </div> */}
          </div>
        )}

        <button
          type="submit"
          disabled={
            uploadLoading ||
            (uploadMethod === "file" && !urls[0]) ||
            (uploadMethod === "youtube" && !youtubeLink) ||
            !selectedCategory ||
            !selectedSubcategory ||
            !title
          }
          className="submit-button"
        >
          Create Post
        </button>
      </form>
    </div>
  );
}
